import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import PauseOutlined from "@mui/icons-material/PauseOutlined";
import MicOutlined from "@mui/icons-material/MicOutlined";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import AddOutlined from "@mui/icons-material/AddOutlined";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import VoiceCloneModal from "../voices/VoiceCloneModal";

const AVATAR_FAV_KEY = "ahead_video_agent_avatar_favs";

function loadAvatarFavs() {
  try {
    const raw = localStorage.getItem(AVATAR_FAV_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveAvatarFavs(set) {
  localStorage.setItem(AVATAR_FAV_KEY, JSON.stringify([...set]));
}

function isLikelyUserVoice(v) {
  if (!v || typeof v !== "object") return false;
  if (v.is_owner === true || v.is_custom === true) return true;
  const src = `${v.voice_source || v.type || v.source || ""}`.toLowerCase();
  return (
    src.includes("clone") ||
    src.includes("custom") ||
    src.includes("instant") ||
    src === "user"
  );
}

function isLikelyUserAvatar(a) {
  if (!a || typeof a !== "object") return false;
  if (a.is_owner === true || a.is_my === true || a.is_my_avatar === true) return true;
  const vis = String(a.visibility || "").toLowerCase();
  if (vis === "private" || vis === "mine") return true;
  const t = String(a.type || a.avatar_type || "").toLowerCase();
  return t.includes("private") || t.includes("mine") || t.includes("user");
}

function avatarMatchesCategory(a, chip) {
  if (chip === "All") return true;
  const name = `${a.avatar_name || ""}`.toLowerCase();
  const tags = Array.isArray(a.tags) ? a.tags.join(" ").toLowerCase() : "";
  const blob = `${name} ${tags}`;
  if (chip === "Professional") {
    return /suit|office|business|doctor|lawyer|formal|executive|corporate|blazer|nurse|training|biz|desk/.test(
      blob,
    );
  }
  if (chip === "Lifestyle") {
    return /casual|sofa|home|kitchen|gym|yoga|lounge|couch|coffee|outdoor|sport|picnic|park/.test(blob);
  }
  if (chip === "UGC") return /ugc|selfie|phone|real|authentic|creator/.test(blob);
  if (chip === "Community") return true;
  if (chip === "Favorites") return false;
  return true;
}

function normalizeStyleRow(row) {
  if (!row || typeof row !== "object") return null;
  const id =
    row.style_id != null
      ? String(row.style_id)
      : row.id != null
        ? String(row.id)
        : row.template_id != null
          ? String(row.template_id)
          : "";
  if (!id) return null;
  const name = String(row.name || row.title || row.label || "Style");
  const thumb =
    String(row.preview_url || row.thumbnail_url || row.cover_url || row.image_url || "") || null;
  const category = String(row.category || row.group || row.tag || "All Styles");
  return { id, name, thumb, category };
}

const FALLBACK_STYLES = [
  { id: "__fb_cinematic__", name: "Cinematic opener", category: "Cinematic", color: "#1e3a5f" },
  { id: "__fb_minimal__", name: "Minimal bold type", category: "Print", color: "#2d2d2d" },
  { id: "__fb_product__", name: "Product spotlight", category: "Retro Tech", color: "#4a1942" },
  { id: "__fb_ugc__", name: "UGC handheld", category: "Pop Culture", color: "#3d2914" },
  { id: "__fb_corporate__", name: "Corporate clean", category: "Professional", color: "#1a237e" },
  { id: "__fb_warm__", name: "Warm storytelling", category: "Handmade", color: "#4e342e" },
];

/** @param {{ open: boolean; onClose: () => void; lang: object; avatars: any[]; selectedLookId: string; onPick: (row: any) => void }} props */
export function ChooseAvatarDialog({ open, onClose, lang, avatars, selectedLookId, onPick }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = React.useState(1);
  const [q, setQ] = React.useState("");
  const [chip, setChip] = React.useState("All");
  const [favMenuEl, setFavMenuEl] = React.useState(null);
  const [favs, setFavs] = React.useState(() => loadAvatarFavs());

  const chips = ["All", "Professional", "Lifestyle", "UGC", "Community", "Favorites"];

  const hasOwnershipTabs = React.useMemo(
    () => (avatars || []).some((a) => a._vaOwnership === "mine" || a._vaOwnership === "public"),
    [avatars],
  );

  const myList = React.useMemo(() => {
    if (hasOwnershipTabs) return (avatars || []).filter((a) => a._vaOwnership === "mine");
    return (avatars || []).filter(isLikelyUserAvatar);
  }, [avatars, hasOwnershipTabs]);

  const publicList = React.useMemo(() => {
    if (hasOwnershipTabs) return (avatars || []).filter((a) => a._vaOwnership === "public");
    return (avatars || []).filter((a) => !isLikelyUserAvatar(a));
  }, [avatars, hasOwnershipTabs]);

  const base = tab === 0 ? myList : publicList;

  const filtered = React.useMemo(() => {
    const qq = q.trim().toLowerCase();
    return base.filter((a) => {
      if (chip === "Favorites") {
        const id = String(a.avatar_id || "");
        if (!favs.has(id)) return false;
      } else if (!avatarMatchesCategory(a, chip)) return false;
      if (!qq) return true;
      return String(a.avatar_name || "").toLowerCase().includes(qq);
    });
  }, [base, q, chip, favs]);

  const toggleFav = (id) => {
    setFavs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveAvatarFavs(next);
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.98),
          backgroundImage: "none",
          maxHeight: fullScreen ? "100%" : "min(92vh, 880px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" fontWeight={800}>
          {lang?.videoAgentModalAvatarTitle || "Choose an Avatar"}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddOutlined />}
            onClick={() => window.open("https://app.heygen.com/", "_blank", "noopener,noreferrer")}
            sx={{ textTransform: "none", fontWeight: 700, display: { xs: "none", sm: "inline-flex" } }}
          >
            {lang?.videoAgentModalCreateAvatar || "Create Avatar"}
          </Button>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
        >
          <Tab label={lang?.videoAgentModalMyAvatars || "My Avatars"} />
          <Tab label={lang?.videoAgentModalPublicAvatars || "Public Avatars"} />
        </Tabs>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: "center" }}>
          <TextField
            fullWidth
            size="small"
            placeholder={lang?.videoAgentModalSearch || "Search"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <IconButton onClick={(e) => setFavMenuEl(e.currentTarget)} sx={{ border: 1, borderColor: "divider" }}>
            <FilterListIcon />
          </IconButton>
          <Menu anchorEl={favMenuEl} open={Boolean(favMenuEl)} onClose={() => setFavMenuEl(null)}>
            <MenuItem
              onClick={() => {
                setChip("Favorites");
                setFavMenuEl(null);
              }}
            >
              {lang?.videoAgentModalShowFavorites || "Show favorites only"}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setChip("All");
                setFavMenuEl(null);
              }}
            >
              {lang?.videoAgentModalClearFilter || "Reset category filter"}
            </MenuItem>
          </Menu>
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {chips.map((c) => (
            <Chip
              key={c}
              label={c}
              onClick={() => setChip(c)}
              color={chip === c ? "primary" : "default"}
              variant={chip === c ? "filled" : "outlined"}
              sx={{ fontWeight: 700 }}
            />
          ))}
        </Stack>
        {tab === 0 && filtered.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            {lang?.videoAgentModalMyAvatarsEmpty ||
              "No personal avatars detected for this account. Try Public Avatars or create one in your studio."}
          </Typography>
        )}
        <Grid container spacing={1.25}>
          {filtered.map((row) => {
            const id = row.avatar_id;
            const img = row.preview_image_url;
            const selected = selectedLookId === id;
            const isFav = favs.has(String(id));
            return (
              <Grid item xs={6} sm={4} md={3} key={id}>
                <Box sx={{ position: "relative" }}>
                  <Box
                    onClick={() => {
                      onPick(row);
                      onClose();
                    }}
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      cursor: "pointer",
                      border: "3px solid",
                      borderColor: selected ? "primary.main" : "transparent",
                      boxShadow: selected ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}` : "none",
                      transition: "transform 0.18s ease, box-shadow 0.18s ease",
                      "&:hover": { transform: "translateY(-3px)", boxShadow: 4 },
                    }}
                  >
                    <Box sx={{ position: "relative", pt: "130%", bgcolor: "grey.900" }}>
                      {img ? (
                        <Box
                          component="img"
                          alt=""
                          src={img}
                          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : null}
                    </Box>
                    <Typography variant="caption" display="block" textAlign="center" noWrap sx={{ py: 0.75, px: 0.5 }}>
                      {row.avatar_name}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      bgcolor: alpha("#000", 0.45),
                      color: "common.white",
                      "&:hover": { bgcolor: alpha("#000", 0.65) },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFav(String(id));
                    }}
                  >
                    {isFav ? "★" : "☆"}
                  </IconButton>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

/** @param {{ open: boolean; onClose: () => void; lang: object; voices: any[]; selectedVoiceId: string; onPick: (id: string) => void; showSnack: Function; voicePreviewPlaying: boolean; voicePreviewUrl: string | null; onTogglePreview: (v: any) => void }} props */
export function ChooseVoiceDialog({
  open,
  onClose,
  lang,
  voices,
  selectedVoiceId,
  onPick,
  showSnack,
  voicePreviewPlaying,
  voicePreviewUrl,
  onTogglePreview,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = React.useState(1);
  const [q, setQ] = React.useState("");
  const [cloneOpen, setCloneOpen] = React.useState(false);

  const myVoices = React.useMemo(() => (voices || []).filter(isLikelyUserVoice), [voices]);
  const lib = React.useMemo(() => (voices || []).filter((v) => !isLikelyUserVoice(v)), [voices]);
  const base = tab === 0 ? myVoices : lib;

  const filtered = React.useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return base;
    return base.filter((v) => String(v.name || "").toLowerCase().includes(qq));
  }, [base, q]);

  return (
    <>
      <VoiceCloneModal open={cloneOpen} onClose={() => setCloneOpen(false)} lang={lang} showSnack={showSnack} />
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 3,
            bgcolor: (t) => alpha(t.palette.background.paper, 0.98),
            maxHeight: fullScreen ? "100%" : "min(90vh, 720px)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" fontWeight={800}>
            {lang?.videoAgentModalVoiceTitle || "Choose a Voice"}
          </Typography>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<MicOutlined />}
              onClick={() => setCloneOpen(true)}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 99, py: 1.25 }}
            >
              {lang?.voicesCloneNav || "Clone your voice"}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AutoFixHighOutlined />}
              onClick={() => window.open("https://app.heygen.com/", "_blank", "noopener,noreferrer")}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 99, py: 1.25 }}
            >
              {lang?.videoAgentModalDesignVoice || "Design a voice"}
            </Button>
          </Stack>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
          >
            <Tab label={lang?.videoAgentModalMyVoices || "My voices"} />
            <Tab label={lang?.voicesTabLibrary || "Our Library"} />
          </Tabs>
          <TextField
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            placeholder={lang?.voicesSearchPlaceholder || "Search voices…"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {tab === 0 && filtered.length === 0 && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems="center"
              sx={{
                py: 3,
                px: 2,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.08 : 0.06),
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 100,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.25),
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography fontWeight={800} sx={{ mb: 0.5 }}>
                  {lang?.videoAgentModalMyVoicesHeadline || "Let your unique voice shine"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {lang?.videoAgentModalMyVoicesSub ||
                    "Clone or design a voice, then it will appear here for Video Agent."}
                </Typography>
                <Button variant="outlined" size="small" onClick={() => setCloneOpen(true)} sx={{ fontWeight: 700 }}>
                  {lang?.videoAgentModalLearnMore || "Learn more"}
                </Button>
              </Box>
            </Stack>
          )}
          <Stack spacing={0.5} sx={{ maxHeight: { xs: "50vh", sm: "360px" }, overflowY: "auto", pr: 0.5 }}>
            {filtered.map((v) => {
              const sel = v.voice_id === selectedVoiceId;
              const playing = voicePreviewPlaying && voicePreviewUrl === v.preview_audio;
              return (
                <Stack
                  key={v.voice_id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  onClick={() => {
                    onPick(v.voice_id);
                    onClose();
                  }}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: sel ? "primary.main" : "divider",
                    bgcolor: sel ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                    "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.04) },
                  }}
                >
                  <IconButton
                    size="small"
                    disabled={!v.preview_audio}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTogglePreview(v);
                    }}
                  >
                    {playing ? <PauseOutlined fontSize="small" /> : <PlayArrowOutlined fontSize="small" />}
                  </IconButton>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={700} noWrap>
                      {v.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {[v.language, v.gender].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** @param {{ open: boolean; onClose: () => void; lang: object; apiStyles: any[]; onPick: (style: { id: string; name: string; thumb: string | null } | null) => void; selectedStyleId: string | null }} props */
export function ChooseStyleDialog({ open, onClose, lang, apiStyles, onPick, selectedStyleId }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = React.useState(0);
  const [chip, setChip] = React.useState("All Styles");
  const [q, setQ] = React.useState("");

  const normalized = React.useMemo(() => {
    const fromApi = (apiStyles || []).map(normalizeStyleRow).filter(Boolean);
    if (fromApi.length) return fromApi;
    return FALLBACK_STYLES.map((s) => ({ id: s.id, name: s.name, thumb: null, category: s.category, color: s.color }));
  }, [apiStyles]);

  const chips = React.useMemo(() => {
    const s = new Set(["All Styles"]);
    normalized.forEach((x) => s.add(x.category || "All Styles"));
    return [...s];
  }, [normalized]);

  const filtered = React.useMemo(() => {
    const qq = q.trim().toLowerCase();
    return normalized.filter((x) => {
      if (chip !== "All Styles" && x.category !== chip) return false;
      if (qq && !String(x.name).toLowerCase().includes(qq)) return false;
      return true;
    });
  }, [normalized, chip, q]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.98),
          maxHeight: fullScreen ? "100%" : "min(92vh, 900px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" fontWeight={800}>
          {lang?.videoAgentModalStyleTitle || "Choose a Style or Brand System"}
        </Typography>
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
        >
          <Tab label={lang?.videoAgentModalStyleTab || "Style"} />
          <Tab label={lang?.videoAgentModalBrandTab || "Brand System"} />
        </Tabs>
        {tab === 1 ? (
          <Stack alignItems="center" sx={{ py: 6, textAlign: "center" }} spacing={2}>
            <Typography color="text.secondary" maxWidth={480}>
              {lang?.videoAgentModalBrandEmpty ||
                "Brand systems are managed in your studio. Open it to upload logos, colors, and templates — then pick a style here."}
            </Typography>
            <Button variant="contained" href="https://app.heygen.com/" target="_blank" rel="noopener noreferrer">
              {lang?.videoAgentOpenHeyGen || "Open studio"}
            </Button>
          </Stack>
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              placeholder={lang?.videoAgentModalSearch || "Search"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {chips.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  onClick={() => setChip(c)}
                  color={chip === c ? "primary" : "default"}
                  variant={chip === c ? "filled" : "outlined"}
                  sx={{ fontWeight: 700 }}
                />
              ))}
            </Stack>
            <Grid container spacing={1.25}>
              <Grid item xs={6} sm={4} md={3}>
                <Box
                  onClick={() => {
                    onPick(null);
                    onClose();
                  }}
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                    cursor: "pointer",
                    border: "2px solid",
                    borderColor: !selectedStyleId ? "primary.main" : "divider",
                    textAlign: "center",
                    minHeight: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography fontWeight={800}>{lang?.videoAgentStyleAutoCard || "Auto"}</Typography>
                </Box>
              </Grid>
              {filtered.map((st) => {
                const sel = selectedStyleId === st.id;
                const bg = st.thumb
                  ? undefined
                  : st.color || alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.5 : 0.12);
                return (
                  <Grid item xs={6} sm={4} md={3} key={st.id}>
                    <Box
                      onClick={() => {
                        onPick(st);
                        onClose();
                      }}
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        cursor: "pointer",
                        border: "3px solid",
                        borderColor: sel ? "error.main" : "transparent",
                        position: "relative",
                        transition: "transform 0.18s ease",
                        "&:hover": { transform: "translateY(-2px)" },
                      }}
                    >
                      <Box sx={{ position: "relative", pt: "72%", bgcolor: bg }}>
                        {st.thumb ? (
                          <Box
                            component="img"
                            alt=""
                            src={st.thumb}
                            sx={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              px: 1,
                              textAlign: "center",
                              color: "common.white",
                              fontWeight: 800,
                            }}
                          >
                            {st.name}
                          </Typography>
                        )}
                      </Box>
                      {st.thumb ? (
                        <Typography variant="caption" display="block" textAlign="center" sx={{ py: 0.5 }} noWrap>
                          {st.name}
                        </Typography>
                      ) : null}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Photo classic: pick talking photo + script snippet */
/** @param {{ open: boolean; onClose: () => void; lang: object; talkingPhotos: any[]; selectedTalkingPhotoId: string; onPickPhoto: (id: string) => void; script: string; onScript: (s: string) => void }} props */
export function PhotoClassicDialog({
  open,
  onClose,
  lang,
  talkingPhotos,
  selectedTalkingPhotoId,
  onPickPhoto,
  script,
  onScript,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const groups = React.useMemo(
    () =>
      (talkingPhotos || []).map((p) => ({
        id: p.talking_photo_id,
        name: p.talking_photo_name,
        img: p.preview_image_url,
      })),
    [talkingPhotos],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 1 }}>
        <Typography fontWeight={800}>{lang?.videoAgentTabPhoto || "Photo avatar"}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {lang?.videoAgentPhotoClassicHint ||
            "Choose a talking photo and the exact script. Uses classic rendering (not Video Agent)."}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          sx={{ mb: 2 }}
          label={lang?.videoAgentPhotoScriptLabel || "Script"}
          value={script}
          onChange={(e) => onScript(e.target.value)}
        />
        <Grid container spacing={1}>
          {groups.map((g) => (
            <Grid item xs={4} key={g.id}>
              <Box
                onClick={() => onPickPhoto(g.id)}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "pointer",
                  border: "3px solid",
                  borderColor: selectedTalkingPhotoId === g.id ? "primary.main" : "transparent",
                }}
              >
                <Box sx={{ pt: "120%", position: "relative", bgcolor: "grey.200" }}>
                  {g.img ? (
                    <Box
                      component="img"
                      src={g.img}
                      alt=""
                      sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : null}
                </Box>
                <Typography variant="caption" noWrap display="block" textAlign="center" sx={{ py: 0.5 }}>
                  {g.name}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Button fullWidth variant="contained" sx={{ mt: 2, fontWeight: 800 }} onClick={onClose}>
          {lang?.videoAgentPhotoClassicDone || "Done"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
